import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { navMode, navVisible } from '../../api/nav'
import TrendFeedSection, { type TrendFeedCardData } from '../../components/TrendFeedSection.tsx'
import { useAuth } from '../../context/AuthContext'
import { useNavModes } from '../../hooks/useNavModes'
import { useProviders } from '../../hooks/useProviders.ts'
import { gatePromoByCost } from '../../utils/discount'
import { cardInputVideoContext, computeModelCost } from '../../utils/modelCost'
import { sectionIdForPath } from '../../utils/navSection'
import { isStaff } from '../../utils/roles'
import { buildFeedBlocks, mulberry32 } from '../bots/hooks/useFeedBlocks.ts'
import type { PopularSectionBase } from '../bots/hooks/usePopularSections.ts'
import { cardPrimaryUrl, cardVisibleOn, type ToolPage } from '../bots/utils/cardSeo.ts'
import { findTrendMemberHome, makeFindModel, type BackendCard } from '../bots/utils/popularCardApply.ts'
import { isWideBlock, type HomeSection } from './homeBlocks'

const SOURCE_PAGES: ToolPage[] = ['image', 'video', 'audio', 'trends', 'photostudio']

interface Props {
	section: HomeSection
	/** Общий пул карточек «Конструктора промптов» из той же выдачи страницы. */
	rawPool: BackendCard[]
}

/** Лента пресетов во вкладке главной — на том же движке, что ленты разделов /bots: состав вручную
 *  или по правилу, порядок, «главная карточка», все раскладки и размеры (buildFeedBlocks +
 *  TrendFeedSection). Отличие одно: у вкладки нет своего раздела карточек, поэтому авто-правила
 *  берут пул раздела из настройки блока `source_page`; ручной состав от раздела не зависит.
 *
 *  Плитка — настоящая ссылка на канонический лендинг инструмента. Лендинга нет (карточка без раздела,
 *  заведена только ради главной) или его раздел закрыт этому зрителю — ведём в генератор с применённой
 *  карточкой: он к режимам разделов не привязан. То же правило на сервере у цели «карточка»
 *  (backend/app/services/link_targets.py). */
export default function HomeFeedBlock({ section, rawPool }: Props) {
	const navigate = useNavigate()
	const { brands } = useProviders()
	const { modes } = useNavModes()
	const { user } = useAuth()
	const staff = isStaff(user?.role)
	const findModel = useMemo(() => makeFindModel(brands), [brands])
	// Сид перемешивания: стабилен между ре-рендерами, новый — при новом заходе (как в useFeedBlocks).
	const [seed] = useState(() => Math.floor(Math.random() * 0x7fffffff))

	const block = useMemo(() => {
		const requested = String(section.config?.source_page || '')
		// Ручной состав кураторский, как на трендах: видимость карточки в её разделе его не режет.
		const page: ToolPage = SOURCE_PAGES.includes(requested as ToolPage) ? (requested as ToolPage) : 'trends'
		const lanePool = rawPool.filter((c) => cardVisibleOn(c, page))
		return buildFeedBlocks({ page, sections: [section as PopularSectionBase], rawPool, lanePool }, mulberry32(seed))[0] ?? null
	}, [section, rawPool, seed])

	const gate = useCallback(
		<T extends BackendCard>(c: T): T => {
			const m = c.target_adapter && c.target_model ? findModel(c.target_adapter, c.target_model) : null
			return gatePromoByCost(c, computeModelCost(m, c.settings, cardInputVideoContext(m, c)))
		},
		[findModel],
	)

	const hrefOf = useCallback(
		(c: TrendFeedCardData): string | undefined => {
			// Герой ленты — участник мульти-тренда (из пула спрятан): адрес его группы, участник — в state клика.
			const raw = rawPool.find((x) => x.id === c.id) ?? (c.slug ? findTrendMemberHome(rawPool, c.slug)?.group : undefined)
			if (!raw) return undefined
			const landing = cardPrimaryUrl(raw)
			if (landing) {
				const sectionId = sectionIdForPath(landing)
				// Карта режимов ещё не пришла — не мешаем: страж только запрещает, а не открывает.
				if (!sectionId || !modes || navVisible(navMode(modes, sectionId), staff)) return landing
			}
			if (raw.card_kind === 'flow' || !raw.slug) return undefined
			return `/generate?tool=${encodeURIComponent(raw.slug)}`
		},
		[rawPool, modes, staff],
	)

	if (!block) return null
	return (
		<TrendFeedSection
			title={block.title}
			subtitle={block.subtitle}
			icon={block.icon}
			layout={block.arrangement}
			tile={block.tile}
			cards={block.members.map(gate)}
			visibleCount={block.visibleCount}
			visibleRows={block.visibleRows}
			cardSize={block.cardSize}
			theme={block.theme}
			bgColor={block.bgColor}
			heroCard={block.hero ? gate(block.hero) : block.hero}
			hoverAction={section.config?.hover_action === true}
			fullWidth={isWideBlock(section)}
			onPick={(c) => {
				const url = hrefOf(c)
				if (!url) return
				const home = rawPool.some((x) => x.id === c.id) ? null : (c.slug ? findTrendMemberHome(rawPool, c.slug) : null)
				navigate(url, home ? { state: { trendMemberId: home.memberId, skipAnchor: true } } : undefined)
			}}
			onPickMember={(c, memberId) => {
				// Кадр версии на плитке группы (23.10): её лендинг сразу с этой версией.
				const url = hrefOf(c)
				if (url) navigate(url, { state: { trendMemberId: memberId, skipAnchor: true } })
			}}
			cardHref={hrefOf}
		/>
	)
}
